import { z } from 'zod';

import {
  indexSearchSchema,
  listParams,
  type ListParams,
} from '@nucleus-commerce/admin-kit/index-table';

import type { EventListParams } from '../api/analytics-api';
import { EVENT_FILTER_FIELDS } from '../components/event-filters';

/**
 * URL search of the analytics tab's event log; the reports layout owns `range`, `from` and `to`.
 * The endpoint has no text search and no sort. The router keeps a param the schema leaves out
 * and the list state would send it, so `q` and `sort` are dropped explicitly.
 */
export const eventListSearchSchema = indexSearchSchema(EVENT_FILTER_FIELDS).extend({
  q: z.undefined().catch(undefined),
  sort: z.undefined().catch(undefined),
});

export type EventListSearch = z.infer<typeof eventListSearchSchema>;

/**
 * The events request for the table's list params. The Occurred filter, when set, is the log's
 * own time range; otherwise the log follows the report's range. Both are UTC days.
 */
export function toEventParams(
  params: ListParams,
  range: { from: string; to: string },
): EventListParams {
  const { occurredAt_gte: gte, occurredAt_lte: lte, ...rest } = params;
  const own = gte !== undefined || lte !== undefined;
  if (!own) return { ...rest, from: range.from, to: range.to };
  return {
    ...rest,
    ...(gte !== undefined ? { from: gte } : {}),
    ...(lte !== undefined ? { to: lte } : {}),
  };
}

/** The request for a validated search, as the loader builds it; the page's list params match it. */
export function eventListParams(
  search: EventListSearch,
  range: { from: string; to: string },
): EventListParams {
  return toEventParams(listParams(search, EVENT_FILTER_FIELDS), range);
}
